import React, { useCallback, useEffect, useState } from 'react';

import { useDisclosure } from '@withjoy/joykit';

import { SearchResultPdpModal } from './components/SearchResultPdpModal/SearchResultPdpModal';
import { SearchResultsGrid } from './components/SearchResultsGrid';
import { ProductType, SearchResultPdpModalForm } from './UniversalProdutcSearch.types';
import { UseSharedCatalogMutationReturn } from '../../Catalog.controller';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useCatalogLoggingContext } from '../../Catalog.logging';
import { withLazyPropOnIntersection } from '@shared/utils/hooks/useLazyLoadImage';
import { PagesForTelemetry } from '../../Catalog.telemetry.types';
import { ProductUniversalSearchQuery, useProductUniversalSearchLazyQuery } from '@graphql/generated';

type UniversalProductSearchProps = {
  /**
   * Intended to only be used for telemetry
   *
   * Total number of products from joy catalog search.
   */
  joyCatalogSearchResultCount: number;

  /**
   * Intended to only be used for telemetry
   *
   * How many products of the joy catalog search are currently visible
   *
   * eg. 737 total search results, but currently only 48 are shown
   */
  visibleJoyCatalogSearchResultsCount: number;
  searchQuery: string;
  onAddProductToRegistry: UseSharedCatalogMutationReturn['addCatalogItemToRegistry'];
  verifyUserAccessAndInvokeCb: (cb: () => void) => void;

  /**
   * 1. Prinmary: When there are search results, the universal search results grid will be placed below
   * 2. Secondary: When there are no search results, we only show the universal search results
   */
  placement: 'primary' | 'secondary';
  telemetryPageValue?: PagesForTelemetry;
  isMobile: boolean;
};

type State = {
  loading: boolean;
  error: unknown;
  data?: ProductUniversalSearchQuery | null;
};

const Root = (props: UniversalProductSearchProps) => {
  const {
    joyCatalogSearchResultCount,
    onAddProductToRegistry,
    placement,
    searchQuery,
    telemetryPageValue,
    verifyUserAccessAndInvokeCb,
    visibleJoyCatalogSearchResultsCount,
    isMobile
  } = props;

  const [{ data, loading }, setState] = useState<State>({ loading: false, data: null, error: null });
  const { onOpen, isOpen, onClose } = useDisclosure();
  const [selectedSearchResult, setSelectedSearchResult] = useState<{ searchResult: ProductType; itemPosition: number } | null>(null);
  const { trackUniversalSearchResultClickAction, trackUniversalSearchInitiated } = useCatalogLoggingContext();
  const [fetchProducts, { data: products }] = useProductUniversalSearchLazyQuery();

  const trackSearchAttempt = useEventCallback(() => {
    trackUniversalSearchInitiated({
      joyCatalogSearchResultsCount: joyCatalogSearchResultCount,
      visibleJoyCatalogSearchResultsCount,
      searchTermOverride: searchQuery,
      page: telemetryPageValue
    });
  });

  useEffect(() => {
    if (searchQuery) {
      setState({
        loading: true,
        data: null,
        error: null
      });
      trackSearchAttempt();
      fetchProducts({ variables: { query: { text: searchQuery } } });
    } else {
      // There is nothing to query for, so reset the state.
      setState({
        data: null,
        loading: false,
        error: null
      });
      onClose();
    }
  }, [trackSearchAttempt, searchQuery, onClose, fetchProducts]);

  useEffect(() => {
    if (products?.productUniversalSearch?.items)
      setState({
        loading: false,
        data: products,
        error: null
      });
  }, [products]);

  const handleOnSearchResultClick = useCallback(
    (itemPosition: number, searchResult: ProductType) => {
      trackUniversalSearchResultClickAction({
        label: 'grid',
        itemPosition,
        searchResult: searchResult,
        gridPlacement: placement,
        joyCatalogSearchResultCount,
        visibleJoyCatalogSearchResultsCount,
        searchTermOverride: searchQuery,
        page: telemetryPageValue
      });

      verifyUserAccessAndInvokeCb(() => {
        setSelectedSearchResult({ itemPosition, searchResult });
        onOpen();
      });
    },
    [
      joyCatalogSearchResultCount,
      placement,
      onOpen,
      searchQuery,
      verifyUserAccessAndInvokeCb,
      telemetryPageValue,
      trackUniversalSearchResultClickAction,
      visibleJoyCatalogSearchResultsCount
    ]
  );

  const handleOnSubmitPdpForm = useEventCallback((result: { searchResult: ProductType; itemPosition: number } | null, formValues: Partial<SearchResultPdpModalForm>) => {
    if (result) {
      const priceAsString = result.searchResult.salePrice?.floatingPointDecimalString ?? result.searchResult.listPrice.floatingPointDecimalString;
      onAddProductToRegistry({
        source: 'universalSearch',
        payload: {
          page: telemetryPageValue,
          itemPosition: result.itemPosition,
          searchTerm: searchQuery,
          totalResultCount: data?.productUniversalSearch?.items?.length ?? -1,
          productUrl: result.searchResult.url,
          productImageUrl: result.searchResult.photos[0].url,
          productTitle: result.searchResult.title,
          shopName: result.searchResult.id,
          joyCatalogSearchResultsCount: joyCatalogSearchResultCount,
          visibleJoyCatalogSearchResultsCount,
          searchResultType: 'general',
          qty: formValues.quantity ?? 1,
          title: formValues.title ?? result.searchResult.title,
          note: formValues.note ?? '',
          priceAsFloatingPointDecimalString: priceAsString,
          metadata: {
            source: 'googleShoppingSearch',
            sourceMetadata: {
              scannedPrice: priceAsString
            }
          }
        }
      });
      onClose();
    }
  });

  return (
    <>
      <SearchResultsGrid
        isMobile={isMobile}
        data={data || null}
        loading={loading}
        onSearchResultClick={handleOnSearchResultClick}
        onQuickAddClick={args => {
          const result = { searchResult: args.searchResult, itemPosition: args.itemPosition };
          handleOnSubmitPdpForm(result, { quantity: args.quantity });
        }}
        hideHeader={placement === 'primary'}
      />
      <SearchResultPdpModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmitForm={formValues => handleOnSubmitPdpForm(selectedSearchResult, formValues)}
        searchResult={selectedSearchResult?.searchResult || null}
      />
    </>
  );
};

const Wrapper = withLazyPropOnIntersection(
  React.forwardRef<HTMLDivElement, UniversalProductSearchProps & { isOnScreen: boolean }>((props, ref) => {
    return (
      <div data-id="usearch" ref={ref}>
        {props.isOnScreen ? <Root {...props} /> : null}
      </div>
    );
  }),
  'isOnScreen',
  1250
);

export const UniversalProductSearch = (props: UniversalProductSearchProps) => {
  return (
    <Wrapper
      {...props}
      isOnScreen={{
        onscreen: true,
        offscreen: false
      }}
    />
  );
};
